import { AudioSegmentData } from './AudioProject';

export const UltimateGenerateRequestDocumentTypeEnum = {
  Podcast: 'podcast',
  Article: 'article',
  Custom: 'custom',
} as const;

export type UltimateGenerateRequestDocumentTypeEnum =
  (typeof UltimateGenerateRequestDocumentTypeEnum)[keyof typeof UltimateGenerateRequestDocumentTypeEnum];
export const UltimateGenerateRequestArticleTypeEnum = {
  NewsReport: 'news_report',
  Collector: 'collector',
  Advice: 'advice',
} as const;

export type UltimateGenerateRequestArticleTypeEnum =
  (typeof UltimateGenerateRequestArticleTypeEnum)[keyof typeof UltimateGenerateRequestArticleTypeEnum];
export const UltimateGenerateRequestSearchTypeEnum = {
  Search: 'search',
  News: 'news',
} as const;

export type UltimateGenerateRequestSearchTypeEnum =
  (typeof UltimateGenerateRequestSearchTypeEnum)[keyof typeof UltimateGenerateRequestSearchTypeEnum];
export const UltimateGenerateRequestSearchTbsEnum = {
  QdrH: 'qdr:h',
  QdrD: 'qdr:d',
  QdrW: 'qdr:w',
  QdrM: 'qdr:m',
  QdrY: 'qdr:y',
} as const;

export type UltimateGenerateRequestSearchTbsEnum =
  (typeof UltimateGenerateRequestSearchTbsEnum)[keyof typeof UltimateGenerateRequestSearchTbsEnum];
export const UltimateGenerateRequestPromptLanguageEnum = {
  En: 'en',
  De: 'de',
} as const;

export type UltimateGenerateRequestPromptLanguageEnum =
  (typeof UltimateGenerateRequestPromptLanguageEnum)[keyof typeof UltimateGenerateRequestPromptLanguageEnum];

export interface UltimateGenerateRequest {
  /**
   * Type of document to generate
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  document_type?: UltimateGenerateRequestDocumentTypeEnum;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  topic?: string | null;
  /**
   *
   * @type {Array<string>}
   * @memberof UltimateGenerateRequest
   */
  sources?: string[] | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  user_text?: string | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  context?: string | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  article_type?: UltimateGenerateRequestArticleTypeEnum | null;
  /**
   *
   * @type {number}
   * @memberof UltimateGenerateRequest
   */
  number_of_sections?: number | null;
  /**
   *
   * @type {number}
   * @memberof UltimateGenerateRequest
   */
  number_of_guests?: number | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  podcast_name?: string | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  search_type?: UltimateGenerateRequestSearchTypeEnum | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  search_tbs?: UltimateGenerateRequestSearchTbsEnum | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  prompt_language?: UltimateGenerateRequestPromptLanguageEnum | null;
  /**
   *
   * @type {string}
   * @memberof UltimateGenerateRequest
   */
  llm?: string | null;
}
export interface UltimateGenerateResponse {
  short_title: string;
  one_liner: string;
  main_text: string;
  evidence_info: ScriptEvidence[];
}
export interface BrainstormingBody {
  topic: string;
  context?: string;
  alreadyGeneratedConcepts?: string[];
  llm?: string;
}

export interface BrainstormingResult {
  concepts: string[];
}

export interface GetOutlineInputTopicBody {
  topic: string;
  context?: string;
  concept?: string[];
  numberOfSections?: number;
  numSearchQueries?: number;
  numSearchWebsites?: number;
  llm?: string;
}

export interface GetOutlineInputTextBody {
  mainSource: string;
  context?: string;
  numberOfSections?: number;
  llm?: string;
}

export interface GetOutlineInputTextResult {
  outline: string;
  short_title: string;
  one_liner: string;
}

export interface GetScriptInputTextBody {
  mainSource: string;
  outline?: string;
  podcastName: string;
  context?: string;
  numberOfSections?: number;
  numberOfGuests?: number;
  llm?: string;
}

export interface GetScriptInputTextResult {
  script: string;
}

export interface ScriptEvidence {
  citation: number;
  content: string;
  url: string;
  word_count: number;
}
export interface GetOutlineInputTopicResult {
  outline: string;
  evidence: ScriptEvidence[];
  short_title: string;
  one_liner: string;
}

export interface GetScriptInputTopicBody {
  topic: string;
  outline?: string;
  podcastName: string;
  context?: string;
  numberOfGuests?: number;
  numQuestions?: number;
  numSearchQueries?: number;
  numSearchWebsites?: number;
  llm?: string;
  promptLanguage?: UltimateGenerateRequestPromptLanguageEnum;
  inputUrlOrTextList?: string[];
}

export interface GetScriptInputTopicResult {
  script: string;
  evidence: ScriptEvidence[];
}

export interface GetArticleInputUrlOrTextBody {
  inputUrlOrTextList: string[];
  userTextType: string;
  context: string;
  promptLanguage?: string;
}

export interface GetArticleInputUrlOrTextResult {
  article: string;
  evidence: ScriptEvidence[];
  short_title?: string;
  one_liner?: string;
  inconsistency?: string;
}

export interface GetFeedbackResult {
  overall_feedback: string;
  feedback_list: {
    fid: number;
    criterion: string;
    recommendation: string;
    feedbacktype: string;
    current: string;
    suggested: string;
  }[];
}

export interface ImproveScriptResult {
  script: string;
  explanation: string;
}

export interface AddUniqueFactsResult {
  script: string;
  evidence: ScriptEvidence[];
  questions: string[];
}

export interface SearchUniqueFactsResult {
  evidence: ScriptEvidence[];
  questions: string[];
}

export interface IntegrateFactsResult {
  script: string;
  reasonings: string[];
}

export interface FixIssuesResult {
  article: string;
  explanation: string;
}

export interface IssueItem {
  citation: string;
  description: string;
  instruction: string;
  original_fact: string;
}

export interface SearchMissingFactsResult {
  missing_facts_description: string;
  missing_facts_list: IssueItem[];
}

export interface GenerateBulletpointsResult {
  script: string;
  explanation: string;
}

export interface ImproveScriptWithCustomInstructionResult {
  script: string;
  explanation: string;
}

export interface ImproveTextBasedOnUserCustomInstructionAndExtraContentResult {
  article: string;
  explanation: string;
}

export interface PhoneticSpellingPolishResult {
  script: string;
  sections: AudioSegmentData[];
}

export interface GetArticleInputTopicResult {
  article: string;
  evidence?: ScriptEvidence[];
  short_title: string;
  one_liner: string;
}

export interface GetArticleInputTextResult {
  article: string;
  short_title: string;
  one_liner: string;
}

export interface CheckInconsistenciesResult {
  inconsistencies_description: string;
  inconsistencies_list: IssueItem[];
}

export interface CheckProblemsResult {
  problems_description: string;
  problems_list: IssueItem[];
}
